import React, { useState, useEffect } from 'react';
import { BlockGenerationChart } from '../components/BlockGeneration';
import { blockGenerationService, FlatlineDetectionData } from '../services/blockGenerationService';
import { exportService } from '../services/exportService';

const BlockGenerationDashboard: React.FC = () => {
  const [viewMode, setViewMode] = useState<'generation' | 'cuf'>('generation');
  const [dateRange, setDateRange] = useState('2024-10-05_to_2024-10-11');
  const [plantId, setPlantId] = useState<string>('');
  const [flatlineData, setFlatlineData] = useState<FlatlineDetectionData | null>(null);
  const [chartData, setChartData] = useState<any>(null);

  useEffect(() => {
    loadFlatlineData();
  }, []);

  const loadFlatlineData = async () => {
    try {
      const data = await blockGenerationService.getFlatlineDetection();
      setFlatlineData(data);
    } catch (error) {
      console.error('Error loading flatline data:', error);
    }
  };

  const handleExport = async (format: 'csv' | 'png') => {
    if (format === 'csv') {
      try {
        const data = await blockGenerationService.getDailyGeneration(plantId, dateRange);
        const filename = `block_generation_${dateRange}_${new Date().toISOString().split('T')[0]}.csv`;
        exportService.exportToCSV(data, filename);
      } catch (error) {
        console.error('Error exporting CSV:', error);
        alert('Error exporting data. Please try again.');
      }
    } else if (format === 'png') {
      const filename = `block_generation_chart_${dateRange}_${new Date().toISOString().split('T')[0]}.png`;
      exportService.exportToPNG('block-generation-chart', filename);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Block-wise Generation - RMS Dashboard</h1>
        <div className="flex gap-3">
          <button
            onClick={() => handleExport('csv')}
            className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Export CSV
          </button>
          <button
            onClick={() => handleExport('png')}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Export PNG
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h2 className="text-lg font-bold text-slate-800 mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-xl bg-white/80 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
            >
              <option value="2024-10-05_to_2024-10-11">Oct 5 - Oct 11, 2024</option>
              <option value="2024-10-01_to_2024-10-07">Oct 1 - Oct 7, 2024</option>
              <option value="2024-09-25_to_2024-10-01">Sep 25 - Oct 1, 2024</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Plant
            </label>
            <select
              value={plantId}
              onChange={(e) => setPlantId(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-xl bg-white/80 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
            >
              <option value="">RMS (All Blocks)</option>
              <option value="1">RMS Plant 1</option>
              <option value="2">RMS Plant 2</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Metric
            </label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as 'generation' | 'cuf')}
              className="w-full p-3 border border-slate-300 rounded-xl bg-white/80 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
            >
              <option value="generation">Generation (kWh)</option>
              <option value="cuf">CUF (%)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            Daily Block Generation {viewMode === 'cuf' ? '& CUF' : ''}
          </h2>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('generation')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                viewMode === 'generation'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Generation
            </button>
            <button
              onClick={() => setViewMode('cuf')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                viewMode === 'cuf'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              CUF View
            </button>
          </div>
        </div>
        
        <div id="block-generation-chart">
          <BlockGenerationChart
            plantId={plantId}
            dateRange={dateRange}
            viewMode={viewMode}
          />
        </div>
        
        {/* CUF Standards Compliance Note */}
        {viewMode === 'cuf' && (
          <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
            <p className="text-sm text-blue-700">
              <strong>Standards Compliance:</strong> CUF calculations follow IEC 61724 standards with 
              temperature coefficient adjustments and irradiance normalization.
            </p>
          </div>
        )}
      </div>

      {/* Intelligence Layer Info */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold mb-4">Intelligence Layer</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-red-50 rounded-lg">
            <h3 className="font-medium text-red-800">Flatline Detection</h3>
            <p className="text-sm text-red-600 mt-1">
              {flatlineData ? `${flatlineData.total_flatline_blocks} blocks detected` : 'Loading...'}
            </p>
            <p className="text-xs text-red-500 mt-1">
              Identifies blocks with repeated generation values
            </p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-800">CUF Normalization</h3>
            <p className="text-sm text-blue-600 mt-1">
              Active for all blocks
            </p>
            <p className="text-xs text-blue-500 mt-1">
              Adjusts CUF based on irradiance and temperature
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <h3 className="font-medium text-green-800">Performance Banding</h3>
            <p className="text-sm text-green-600 mt-1">
              High/Medium/Low classification
            </p>
            <p className="text-xs text-green-500 mt-1">
              Classifies blocks into performance categories
            </p>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg">
            <h3 className="font-medium text-yellow-800">Anomaly Alerts</h3>
            <p className="text-sm text-yellow-600 mt-1">
              Real-time monitoring active
            </p>
            <p className="text-xs text-yellow-500 mt-1">
              Triggers alerts for zero generation or abnormal CUF
            </p>
          </div>
        </div>
        
        {/* Flatline Details */}
        {flatlineData && flatlineData.flatline_blocks.length > 0 && (
          <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
            <h3 className="font-medium text-red-800 mb-2">Detected Flatline Blocks</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {flatlineData.flatline_blocks.map((block, index) => (
                <div key={index} className="text-sm text-red-700">
                  <span className="font-medium">{block.block_id}</span>: {block.generation_kwh} kWh 
                  <span className="text-red-500">({block.count} days)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockGenerationDashboard;